import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { version } from '../../../package.json';
import { environment } from '../../environments/environment';

export const APP_VERSION_HEADER = 'X-App-Version';
export const SCREEN_HEADER = 'X-Mkg-Screen';
export const OPERATION_ID_HEADER = 'X-Operation-Id';
export const FAULT_INJECTION_HEADER = 'X-Fault-Injection';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

@Injectable({
  providedIn: 'root'
})
export class AppRequestHeadersService {

  private readonly _injector = inject(Injector);

  readonly appVersion: string = version;

  isApiUrl(url: string): boolean {
    const apiUrl = environment.mkgoURL;
    if (!apiUrl || !url?.startsWith(apiUrl)) {
      return false;
    }
    const next = url.charAt(apiUrl.length);
    return next === '' || next === '/' || next === '?' || apiUrl.endsWith('/');
  }

  isMutating(method: string): boolean {
    return MUTATING_METHODS.has(method?.toUpperCase());
  }

  build(method: string, operationId?: string | null): Record<string, string> {
    const headers: Record<string, string> = {
      [APP_VERSION_HEADER]: this.appVersion,
      [SCREEN_HEADER]: this.currentScreen(),
    };
    if (this.isMutating(method)) {
      headers[OPERATION_ID_HEADER] = operationId || uuidv4();
    }
    if (environment.production === false && typeof localStorage !== 'undefined') {
      const faultInjection = localStorage.getItem('mkg.faultInjection');
      if (faultInjection !== null) {
        headers[FAULT_INJECTION_HEADER] = faultInjection;
      }
    }
    return headers;
  }

  currentScreen(): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null = this._router()?.routerState?.snapshot?.root ?? null;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        segments.push(path);
      }
      route = route.firstChild;
    }
    return `/${segments.join('/')}`;
  }

  private _router(): Router | null {
    return this._injector.get(Router, null);
  }
}
